import React from 'react';
import { Box, Button, Typography, TextField, Autocomplete } from "@mui/material";

export default function TransferPopupContent({
  fromUser,
  toUser,
  transferAmount,
  fromUserData,
  toUserData,
  validationErrors,
  loading,
  usersList,
  onFromUserChange,
  onToUserChange,
  onTransferAmountChange,
  onTransferSubmit,
  onCancel
}) {
  // Filter users for "To" field - exclude the selected "From" user
  const getToUserOptions = () => {
    if (!fromUser) return usersList;
    return usersList.filter(user => user.email.toLowerCase() !== fromUser.toLowerCase());
  };

  // Find user object by email
  const findUserByEmail = (email) => {
    return usersList.find(user => user.email.toLowerCase() === email.toLowerCase());
  };

  return (
    <Box sx={{ padding: 3, minWidth: 650 }}>
      {/* From and To Fields Row */}
      <Box sx={{ display: 'flex', gap: 3, marginBottom: 2 }}>
        {/* From Field */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              marginBottom: 1, 
              fontWeight: 500,
              color: '#08162A'
            }}
          >
            From <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Autocomplete
            options={usersList}
            getOptionLabel={(option) => option.email}
            value={findUserByEmail(fromUser) || null}
            onChange={(event, newValue) => {
              onFromUserChange(newValue ? newValue.email : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="ahmed@gmail.com"
                error={!!validationErrors.fromUser}
                helperText={validationErrors.fromUser}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D1D5DB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3C50E0',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Available Tokens: {option.available_tokens || 0}
                  </Typography>
                </Box>
              </Box>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter(option =>
                option.email.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.first_name && option.first_name.toLowerCase().includes(inputValue.toLowerCase())) ||
                (option.last_name && option.last_name.toLowerCase().includes(inputValue.toLowerCase()))
              );
            }}
            noOptionsText="No users found"
          />
          {fromUserData.email && !validationErrors.fromUser && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#000000', 
                mt: 0.5,
                display: 'block',
                fontSize: '15px',
                fontWeight: 500
              }}
            >
              Available Purchased Tokens: {fromUserData.available_tokens || 0}
            </Typography>
          )}
        </Box>

        {/* To Field */}
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              marginBottom: 1, 
              fontWeight: 500,
              color: '#08162A'
            }}
          >
            To <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Autocomplete
            options={getToUserOptions()}
            getOptionLabel={(option) => option.email}
            value={findUserByEmail(toUser) || null}
            onChange={(event, newValue) => {
              onToUserChange(newValue ? newValue.email : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="ibrahim@gmail.com"
                error={!!validationErrors.toUser}
                helperText={validationErrors.toUser}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F9FAFB',
                    '& fieldset': {
                      borderColor: '#E5E7EB',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D1D5DB',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3C50E0',
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Available Tokens: {option.available_tokens || 0}
                  </Typography>
                </Box>
              </Box>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              return options.filter(option =>
                option.email.toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.first_name && option.first_name.toLowerCase().includes(inputValue.toLowerCase())) ||
                (option.last_name && option.last_name.toLowerCase().includes(inputValue.toLowerCase()))
              );
            }}
            noOptionsText="No users found"
            disabled={!fromUser} // Disable if no "From" user selected
          />
          {toUserData.email && !validationErrors.toUser && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#000000', 
                mt: 0.5,
                display: 'block',
                fontSize: '15px',
                fontWeight: 500
              }}
            >
              Available Purchased Tokens: {toUserData.available_tokens || 0}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Transfer Amount Field */}
      <Box sx={{ marginBottom: 4,paddingTop:3 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            marginBottom: 1, 
            fontWeight: 500,
            color: '#08162A'
          }}
        >
          Enter number of token to transfer <span style={{ color: 'red' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          placeholder="45"
          type="number"
          value={transferAmount}
          onChange={onTransferAmountChange}
          error={!!validationErrors.transferAmount}
          helperText={validationErrors.transferAmount}
          inputProps={{ min: 1, max: fromUserData.available_tokens || 0 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#F9FAFB',
              '& fieldset': {
                borderColor: '#E5E7EB',
              },
              '&:hover fieldset': {
                borderColor: '#D1D5DB',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3C50E0',
              },
            },
          }}
        />
        {fromUserData.available_tokens > 0 && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#6B7280', 
              mt: 0.5,
              display: 'block',
              fontSize: '12px'
            }}
          >
            Maximum transferable tokens: {fromUserData.available_tokens}
          </Typography>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          onClick={onTransferSubmit}
          disabled={loading}
          sx={{ 
            backgroundColor: '#3C50E0',
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': { 
              backgroundColor: '#2A3BB7' 
            },
            '&:disabled': { 
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF'
            }
          }}
        >
          {loading ? 'Transferring...' : 'Transfer Now'}
        </Button>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{ 
            color: '#6B7280', 
            borderColor: '#E5E7EB',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              borderColor: '#D1D5DB',
              backgroundColor: '#F9FAFB'
            }
          }}
        >
          Exit
        </Button>
      </Box>
    </Box>
  );
}